import React, { useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface SitelinkAsset {
  text: string;
  confidence?: number;
}

interface SitelinkUrlDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  sitelinks: SitelinkAsset[];
  urls: Record<number, string>;
  onUrlChange: (index: number, url: string) => void;
  isSaving: boolean;
}

export function SitelinkUrlDialog({
  isOpen,
  onClose,
  onSave,
  sitelinks,
  urls,
  onUrlChange,
  isSaving,
}: SitelinkUrlDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Body scroll locking to prevent layout shift from scrollbar
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  // Focus first input when dialog opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    },
    [isSaving, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalTitleId = 'sitelink-url-dialog-title';

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        onClick={handleBackdropClick}
      />

      {/* Dialog container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id={modalTitleId} className="text-lg font-semibold mb-4">
            Add URLs for Sitelinks
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Each sitelink requires a destination URL.
          </p>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {sitelinks.map((asset, index) => (
              <div key={index}>
                <label
                  htmlFor={`sitelink-url-${index}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {asset.text}
                </label>
                <Input
                  ref={index === 0 ? firstInputRef : undefined}
                  id={`sitelink-url-${index}`}
                  value={urls[index] || ''}
                  onChange={(e) => onUrlChange(index, e.target.value)}
                  placeholder="https://example.com/page"
                  type="url"
                  aria-describedby={`sitelink-url-help-${index}`}
                />
                <span id={`sitelink-url-help-${index}`} className="sr-only">
                  Enter the destination URL for {asset.text}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={onClose}
              variant="secondary"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={isSaving}
              variant="primary"
            >
              {isSaving ? 'Saving...' : 'Save Sitelinks'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
